import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type { CreateHelpMessageDto, CreateHelpRequestDto } from "@reactive-resume/dto";

import {
  closeHelpRequest,
  createHelpRequest,
  fetchHelpRequest,
  fetchHelpRequests,
  reopenHelpRequest,
  sendHelpMessage,
} from "./api";

export const HELP_KEYS = {
  all: ["help"] as const,
  list: ["help", "list"] as const,
  detail: (id: string) => ["help", "detail", id] as const,
};

/** Demandes de l'utilisateur connecté (liste complète, messages inclus). */
export const useHelpRequests = () =>
  useQuery({
    queryKey: HELP_KEYS.list,
    queryFn: fetchHelpRequests,
    staleTime: 15_000,
  });

/** Détail d'une demande (thread complet). */
export const useHelpRequest = (id: string) =>
  useQuery({
    queryKey: HELP_KEYS.detail(id),
    queryFn: () => fetchHelpRequest(id),
    enabled: Boolean(id),
  });

export const useCreateHelpRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dto: CreateHelpRequestDto) => createHelpRequest(dto),
    onSuccess: (created) => {
      queryClient.setQueryData(HELP_KEYS.detail(created.id), created);
      queryClient.invalidateQueries({ queryKey: HELP_KEYS.all });
    },
  });
};

export const useSendHelpMessage = (id: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dto: CreateHelpMessageDto) => sendHelpMessage(id, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: HELP_KEYS.detail(id) });
      queryClient.invalidateQueries({ queryKey: HELP_KEYS.list });
    },
  });
};

export const useCloseHelpRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => closeHelpRequest(id),
    onSuccess: (updated) => {
      queryClient.setQueryData(HELP_KEYS.detail(updated.id), updated);
      queryClient.invalidateQueries({ queryKey: HELP_KEYS.list });
    },
  });
};

/** Réouverture d'une demande clôturée/résolue (le problème est réapparu). */
export const useReopenHelpRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => reopenHelpRequest(id),
    onSuccess: (updated) => {
      queryClient.setQueryData(HELP_KEYS.detail(updated.id), updated);
      queryClient.invalidateQueries({ queryKey: HELP_KEYS.list });
    },
  });
};
